import { inject, Injector, runInInjectionContext } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { Program, SyllabusService, SyllabusStoreModel } from '@examdojo/category/v2';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { ExamDojoFeatureFlag, ExamdojoFeatureFlags, getWhitelistedSyllabusSlugs } from '@examdojo/models/feature-flag';
import { mapToVoid } from '@examdojo/rxjs';
import { UserQuery } from '@examdojo/user';
import { isNotNullish } from '@examdojo/util/nullish';
import { combineLatest, distinctUntilChanged, filter, first, map, Observable, switchMap, tap } from 'rxjs';

export const allSyllabusesResolver: ResolveFn<void> = () => {
  const injector = inject(Injector);

  return runInInjectionContext(injector, () => fetchSyllabusesAndActivate()).pipe(mapToVoid());
};

export const userProgramSyllabusesResolver: ResolveFn<void> = () => {
  const injector = inject(Injector);
  const userQuery = inject(UserQuery);

  return userQuery.active$.pipe(
    first(isNotNullish),
    map((user) => user.program ?? undefined),
    distinctUntilChanged(),
    switchMap((program) => runInInjectionContext(injector, () => fetchSyllabusesAndActivate(program))),
    mapToVoid(),
  );
};

export function fetchSyllabusesAndActivate(program?: Program): Observable<SyllabusStoreModel[]> {
  const syllabusService = inject(SyllabusService);
  const userQuery = inject(UserQuery);
  const user$ = userQuery.active$.pipe(filter(Boolean));
  const errorHandlerService = inject(ErrorHandlerService);
  const featureFlagService = inject(FeatureFlagService<ExamdojoFeatureFlags>);

  const showInactiveSyllabuses = featureFlagService.get(ExamDojoFeatureFlag.ShowInactiveSyllabuses);
  const whitelistedInactiveSyllabusSlugs = getWhitelistedSyllabusSlugs(featureFlagService);

  const syllabuses$ = showInactiveSyllabuses
    ? syllabusService.fetchSyllabuses({ program })
    : syllabusService.fetchSyllabuses({
        program,
        activeOnly: whitelistedInactiveSyllabusSlugs.length === 0,
        syllabusSlugs: whitelistedInactiveSyllabusSlugs,
      });

  return combineLatest([syllabuses$.pipe(first()), user$.pipe(first(Boolean))]).pipe(
    tap(([syllabuses, user]) => {
      if (!syllabuses.length) {
        errorHandlerService.error(`[syllabusResolver]: Empty list of syllabuses returned from the server`);
        return;
      }

      syllabusService.setActive(user.syllabus_id, user.course_level);
    }),
    map(([syllabuses]) => syllabuses),
  );
}
